<template>
    <v-bottom-sheet v-model="visible" :persistent="false" max-width="600">
        <v-card rounded="t-xl" class="install-card">
            <!-- Handle bar -->
            <div class="handle-bar" />

            <!-- Header -->
            <div class="install-header">
                <img src="/icons/icon-192.png" alt="AquaScaper" class="app-icon" />
                <div>
                    <p class="app-name">AquaScaper</p>
                    <p class="app-desc">{{ t('pwa.tagline') }}</p>
                </div>
                <v-btn
                    icon="mdi-close"
                    variant="text"
                    size="small"
                    color="grey"
                    class="ml-auto"
                    @click="dismiss"
                />
            </div>

            <v-divider class="mx-4" />

            <!-- Android — native install prompt available -->
            <div v-if="canInstall" class="install-body">
                <p class="install-desc">{{ t('pwa.android.promptDesc') }}</p>
                <v-btn
                    color="#00838F"
                    rounded="pill"
                    block
                    class="mt-2"
                    @click="triggerInstall"
                >
                    <v-icon start>mdi-cellphone-arrow-down</v-icon>
                    {{ t('pwa.install') }}
                </v-btn>
            </div>

            <!-- Android — manual fallback -->
            <div v-else-if="isAndroid" class="install-body">
                <p class="install-desc">{{ t('pwa.android.manualDesc') }}</p>
                <ol class="install-steps">
                    <li>
                        <span>{{ t('pwa.android.step1') }}</span>
                        <span class="step-icon">⋮</span>
                    </li>
                    <li>{{ t('pwa.android.step2') }}</li>
                    <li>{{ t('pwa.android.step3') }}</li>
                </ol>
            </div>

            <!-- iOS — Safari share sheet -->
            <div v-else-if="isIOS" class="install-body">
                <p class="install-desc">{{ t('pwa.ios.desc') }}</p>
                <ol class="install-steps">
                    <li>
                        <span>{{ t('pwa.ios.step1') }}</span>
                        <v-icon size="18" color="#007AFF">mdi-export-variant</v-icon>
                    </li>
                    <li>{{ t('pwa.ios.step2') }}</li>
                </ol>
                <div class="ios-note">
                    <v-icon size="14" color="grey">mdi-information-outline</v-icon>
                    <span>{{ t('pwa.ios.safariOnly') }}</span>
                </div>
            </div>

            <!-- Desktop fallback — shown when triggered manually from login page -->
            <div v-else class="install-body install-body--desktop">
                <v-icon size="52" color="#00838F" class="mb-3">mdi-cellphone-link</v-icon>
                <p class="install-desc">{{ t('pwa.desktop.desc') }}</p>
                <p class="install-hint">{{ t('pwa.desktop.hint') }}</p>
            </div>
        </v-card>
    </v-bottom-sheet>
</template>

<script lang="ts" setup>
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { usePwaInstall } from "@/composables/usePwaInstall";

const { t } = useI18n();
const { canInstall, install, isIOS, isAndroid, isStandalone, showBanner } = usePwaInstall();

const DISMISSED_KEY = "pwa_install_dismissed";
const dismissed = ref(sessionStorage.getItem(DISMISSED_KEY) === "1");

const visible = computed({
    // showBanner bypasses dismissed + platform checks (explicit user intent from login page)
    get: () => !isStandalone && (showBanner.value || (!dismissed.value && (canInstall.value || isIOS || isAndroid))),
    set: (v: boolean) => { if (!v) dismiss(); },
});

function dismiss() {
    dismissed.value = true;
    showBanner.value = false;
    sessionStorage.setItem(DISMISSED_KEY, "1");
}

async function triggerInstall() {
    await install();
    dismissed.value = true;
}
</script>

<style lang="scss" scoped>
.install-card {
    padding-bottom: env(safe-area-inset-bottom, 12px);
}

.handle-bar {
    width: 36px;
    height: 4px;
    background: rgba(0, 0, 0, 0.15);
    border-radius: 2px;
    margin: 12px auto 8px;
}

.install-header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 12px 16px 16px;
}

.app-icon {
    width: 52px;
    height: 52px;
    border-radius: 12px;
    flex-shrink: 0;
}

.app-name {
    font-size: 1rem;
    font-weight: 700;
    color: var(--color-black-100);
    margin: 0 0 2px;
}

.app-desc {
    font-size: 0.78rem;
    color: var(--color-black-50);
    margin: 0;
}

.install-body {
    padding: 16px 20px 24px;
}

.install-body--desktop {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding-top: 24px;
}

.install-desc {
    font-size: 0.88rem;
    color: var(--color-black-100);
    margin: 0 0 16px;
    line-height: 1.5;
}

.install-hint {
    font-size: 0.82rem;
    color: var(--color-black-50);
    margin: 0;
    line-height: 1.5;
}

.install-steps {
    padding-left: 18px;
    margin: 0 0 8px;

    li {
        font-size: 0.85rem;
        color: var(--color-black-100);
        margin-bottom: 10px;
        display: flex;
        align-items: center;
        gap: 6px;
        line-height: 1.4;
    }
}

.step-icon {
    font-size: 1rem;
    font-weight: 700;
}

.ios-note {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-top: 12px;

    span {
        font-size: 0.72rem;
        color: var(--color-black-50);
    }
}
</style>
